import { datadogRum, RumInitConfiguration, DefaultPrivacyLevel } from '@datadog/browser-rum';
import { getCacheBuster } from '../../utils/get-cache-buster';
import { parseCludoEngineID } from '../../utils/parse-cludo-engine';
import { StringService } from '../string';
import { TeaserService } from '../teaser';
import { GlobalConfigProps, HTMLElementContent, PageTypes } from './types';
import { getTagPageUrl, getWebsiteName, getWhiteListedDomains } from './util';
import { LanguageService } from '../language';
import { PageContextStory, Story } from '../build/types';
import { fixTwitterHandle } from '../../utils/twitter';

export class DomService {
  static createElement(
    tagName: string,
    content: HTMLElementContent = '',
    props: Record<string, string> = {},
  ): HTMLElement {
    const element = document.createElement(tagName);
    element.innerHTML = typeof content === 'string' ? content : content.toString();
    Object.keys(props).forEach((prop) => {
      element.setAttribute(prop, props[prop]);
    });
    return element;
  }

  static activateDatadogMonitoring(): void {
    if (this.isDatadogConfigDefault()) return;

    datadogRum.init(this.getDatadogConfig());
  }

  static pageTypeMap: Record<string, PageTypes> = {
    story: 'Story',
    page: 'Page',
  };

  static getPageType = (isHomepage: boolean, pageType: string): PageTypes => {
    if (isHomepage) return 'Home';
    return this.pageTypeMap[pageType] || 'Other';
  };

  static activateConsentScript(): void {
    const oneTrustScriptPlaceholder = document.getElementById('oneTrustScriptPlaceholder');
    if (!oneTrustScriptPlaceholder) return;

    const activatedOneTrustScript = this.createOneTrustScript();
    document.head.replaceChild(activatedOneTrustScript, oneTrustScriptPlaceholder);
  }

  static getGlobalConfig(story: Story<PageContextStory>): GlobalConfigProps {
    // TODO: check if the story is needed if yes pass items that are needed not the full object.
    const { id: storyblokStoryId, uuid: pageId, pageLanguage: locale, content, allSpaceLocales } = story;

    const language = locale || story.lang;
    const tagPage = LanguageService.getLocaleAwareLink(getTagPageUrl(), language);
    const websiteName = getWebsiteName();
    const whiteListedDomains = getWhiteListedDomains();

    return {
      allSpaceLocales,
      tagPageUrl: tagPage,
      baseDomain: process.env.GATSBY_BASE_DOMAIN,
      brightcoveAccountId: process.env.GATSBY_BRIGHTCOVE_ACCOUNT_ID,
      brightcovePlayerId: process.env.GATSBY_BRIGHTCOVE_PLAYER_ID,
      cludoCustomerID: process.env.GATSBY_CLUDO_CUSTOMER_ID,
      cludoEngineId: this.getCludoEngineId(language),
      cognitoUserpoolClientId: process.env.GATSBY_COGNITO_USERPOOL_CLIENT_ID,
      cognitoUserpoolId: process.env.GATSBY_COGNITO_USERPOOL_ID,
      formsApiUrl: process.env.GATSBY_FORMS_API_URL,
      headerIconUrl: StringService.trimUserInput(process.env.GATSBY_ROCHE_HEADER_ICON_URL),
      locale: language,
      nonDefaultThemeSubcategories: TeaserService.nonDefaultThemeSubcategories,
      pageId: `storyblok:${process.env.GATSBY_STORYBLOK_SPACE_API_KEY_NAME}:${pageId}`,
      recaptchaKey: process.env.GATSBY_GOOGLE_RECAPTCHA_KEY,
      translationUrl: `/translations/${language}.json${getCacheBuster()}`,
      tagsTranslationUrl: `/tags-translations/${language}.json${getCacheBuster()}`,
      popularSearchesTranslationUrl: `/popular-searches-translations/${language}.json${getCacheBuster()}`,
      twitterHandle: fixTwitterHandle(process.env.GATSBY_TWITTER_HANDLE),
      websiteName,
      whiteListedDomains: JSON.stringify(whiteListedDomains),
      isShareHidden: content?.hide_share ?? false,
      gapiClientId: process.env.GATSBY_GAPI_CLIENT_ID,
      gapiKey: process.env.GATSBY_GAPI_KEY,
      cludoFuzziness: process.env.GATSBY_CLUDO_FUZZINESS,
      storyblokStoryId,
      componentLibrary: process.env.GATSBY_ROCHE_COMPONENTS_LIBRARY_URL,
      websiteDomain: StringService.removeProtocolAndSubdomains(process.env.GATSBY_WEBSITE_DOMAIN),
    };
  }

  private static isDatadogConfigDefault(): boolean {
    return (
      process.env.GATSBY_DATADOG_APPLICATION_ID === 'default' ||
      process.env.GATSBY_DATADOG_CLIENT_TOKEN === 'default' ||
      process.env.GATSBY_DATADOG_SERVICE === 'default'
    );
  }

  private static getDatadogConfig(): RumInitConfiguration {
    return {
      applicationId: process.env.GATSBY_DATADOG_APPLICATION_ID!,
      clientToken: process.env.GATSBY_DATADOG_CLIENT_TOKEN!,
      site: 'datadoghq.eu',
      service: process.env.GATSBY_DATADOG_SERVICE!,
      env: process.env.WEBSITE_STAGE || 'test',
      version: process.env.GATSBY_CURRENT_RELEASE_VERSION || 'local',
      sessionSampleRate: 100,
      sessionReplaySampleRate: 20,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: DefaultPrivacyLevel.MASK_USER_INPUT, // Use the correct enum value here
    };
  }

  private static createOneTrustScript(): HTMLScriptElement {
    const script = document.createElement('script');
    const settings = {
      src: 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js',
      type: 'text/javascript',
      charSet: 'UTF-8',
      async: 'true',
      defer: 'true',
      'data-domain-script': `${process.env.GATSBY_ROCHE_ONETRUST_KEY}`,
      'data-document-language': 'true',
    };

    Object.entries(settings).forEach(([name, value]) => script.setAttribute(name, value));
    return script;
  }

  static shouldTriggerConsentEvaluation(records: MutationRecord[]): boolean {
    return records.some((record) =>
      Array.from(record.addedNodes).some((node) => (node as HTMLScriptElement).type === 'text/plain'),
    );
  }

  private static getCludoEngineId(locale: string): string {
    return process.env.GATSBY_CLUDO_ENGINE_ID || parseCludoEngineID(process.env.GATSBY_CLUDO_ENGINE_ID_LIST, locale);
  }
}
